import { createSelector } from '@reduxjs/toolkit';
import { Agent } from '../../../agents/models/Agent';
import { ChatSession } from '../../../chat/models/ChatSession';
import { RootState } from '../../../../redux/store';
import { selectChatSessionIds } from '../../base/chat/selectChatSessionIds';
import { selectChatSessionsById } from '../../base/chat/selectChatSessionsById';
import { selectAgentsById } from '../../base/agents/selectAgentsById';
import { resolveSessionAgent } from '../../../resolvers/resolveSessionAgent';

const selectPinnedAgentIds = (state: RootState) => state.users.pinnedAgentIds;
const MAX_RECENTLY_USED_AGENTS = 4;

export interface RecentlyUsedAgentVm {
    agentId: string;
    name: string;
    agentIconUrl?: string;
}


export const selectRecentlyUsedAgents = createSelector(
    [selectPinnedAgentIds, selectChatSessionIds, selectChatSessionsById, selectAgentsById],
    (
        pinnedAgentIds: string[],
        sessionIds: string[],
        sessionsById: Record<string, ChatSession>,
        agentsById: Record<string, Agent>,
    ): RecentlyUsedAgentVm[] => {
        const seen = new Set<string>();
        const result: RecentlyUsedAgentVm[] = [];

        for (const agentId of pinnedAgentIds) {
            if (result.length >= MAX_RECENTLY_USED_AGENTS) break;

            const agent = agentsById[agentId];
            if (!agent || seen.has(agent.agentId) || agent.agentId === 'progpt-default') continue;

            seen.add(agent.agentId);
            result.push({
                agentId: agent.agentId,
                name: agent.name,
                agentIconUrl: agent.agentIconUrl,
            });
        }

        for (const sessionId of sessionIds) {
            if (result.length >= MAX_RECENTLY_USED_AGENTS) break;

            const session = sessionsById[sessionId];
            if (!session) continue;

            const agent = resolveSessionAgent(session, agentsById);
            if (!agent || seen.has(agent.agentId) || agent.agentId === 'progpt-default') continue;

            seen.add(agent.agentId);
            result.push({
                agentId: agent.agentId,
                name: agent.name,
                agentIconUrl: agent.agentIconUrl,
            });
        }

        return result;
    },
);
